import {FC, PropsWithChildren, useEffect, useRef} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation} from 'react-router'

import HelpSharpIcon from '@mui/icons-material/HelpSharp'
import {Box, Hidden, Theme} from '@mui/material'
import {getProductLineColors, PGButton} from '@progynyglobal/pg-ui-library'
import {makeStyles} from 'tss-react/mui'

import {Overlay} from '@progyny/components/Overlay'
import {HelpContact} from '@progyny/fragments/help/Contact'
import {useNavigation} from '@progyny/hooks/useNavigation'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {RouteEnum, useRoutes} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'
import {logoNavigationState} from '@progyny/store/navigationStore'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    zIndex: 130,
    position: 'fixed',
    bottom: theme.spacing(4),
    right: theme.spacing(4),
  },
  button: {
    borderRadius: 32,
  },
}))

export const HelpButton: FC = () => {
  const {t} = useTranslation()
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const isLoggedIn = useMeStore((s) => s.isLoggedIn)
  const isHelpOverlayOpen = useMeStore((s) => s.isHelpOverlayOpen)
  const toggleHelpOverlay = useMeStore((s) => s.toggleHelpOverlay)
  const companyHasBudget = useMeStore((s) => s.companyHasBudget)
  const {getPath} = useRoutes(companyHasBudget)
  const {setNav, resetNav} = useNavigation()
  const {pathname} = useLocation()
  const {classes} = useStyles()

  const wasOpenRef = useRef(false)

  useEffect(() => {
    if (isHelpOverlayOpen) {
      setNav(logoNavigationState)
    } else if (wasOpenRef.current) {
      resetNav()
    }
    wasOpenRef.current = isHelpOverlayOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHelpOverlayOpen])

  const handleOpen = () => {
    toggleHelpOverlay(true)
  }

  const handleClose = () => {
    toggleHelpOverlay(false)
  }

  const isIntro = pathname.includes('intro')
  const isArticles = pathname.includes(getPath(RouteEnum.ARTICLES))
  const isArticleSegment = pathname.includes(
    getPath(RouteEnum.KNOWLEDGE_CENTER) + '/'
  )
  const isAprylNavigator = pathname.includes(getPath(RouteEnum.APRYL_NAVIGATOR))

  const notAllowedRoutes =
    !isIntro && !isArticles && !isAprylNavigator && !isArticleSegment

  if (
    pathname === '/partner-agreement-accepted' ||
    pathname === '/partner-agreement-declined'
  ) {
    return null
  }

  const helpIcon = (
    <HelpSharpIcon className={isHelpOverlayOpen ? 'highlighted' : undefined} />
  )

  return (
    <>
      <Overlay open={isHelpOverlayOpen} close={handleClose}>
        <HelpContact closeOverlay={handleClose} />
      </Overlay>
      {isLoggedIn && notAllowedRoutes && (
        <Hidden smDown>
          <Box className={classes.root}>
            <PGButton
              sx={{
                backgroundColor: colors.primary,
                color: '#fff',
              }}
              startIcon={helpIcon}
              className={classes.button}
              variant='contained'
              onClick={handleOpen}
              label={t('buttons_help_button_help')}
            />
          </Box>
        </Hidden>
      )}
    </>
  )
}
